<template>
  <div v-if="validAverage" class="flex items-center py-8" :aria-label="ariaLabel" role="img" @click="$emit('star-rating-click')">
    <!-- Full stars -->
    <StarIcon
      v-for="index in fullStars"
      :key="'full-' + index"
      color="#FF0064"
      :width="starSize"
      :height="starSize"
      aria-hidden="true"
    />

    <!-- Half star -->
    <StarHalfFilledIcon v-if="hasHalfStar" color="#FF0064" :width="starSize" :height="starSize" aria-hidden="true" />

    <!-- Empty stars -->
    <StarOutlineIcon
      v-for="index in emptyStars"
      :key="'empty-' + index"
      color="#FF0064"
      :width="starSize"
      :height="starSize"
      aria-hidden="true"
    />

    <!-- Optional count -->
    <span v-if="showCount" class="text-grey-text-subdued text-small ml-4"> ({{ count }}) </span>
  </div>
</template>

<script lang="ts">
import StarIcon from '../../graphics/icons/StarIcon.vue';
import StarHalfFilledIcon from '../../graphics/icons/StarHalfFilledIcon.vue';
import StarOutlineIcon from '../../graphics/icons/StarOutlineIcon.vue';

const LIMIT = 5;

export default {
  name: 'StarRating',
  components: {
    StarIcon,
    StarHalfFilledIcon,
    StarOutlineIcon,
  },
  props: {
    count: {
      type: Number!,
      required: true,
    },
    average: {
      type: Number!,
      required: true,
    },
    showCount: {
      type: Boolean,
      default: true,
    },
    starSize: {
      type: Number,
      default: 24,
    },
  },
  computed: {
    validAverage(): boolean {
      return this.average !== null && this.average >= 0 && this.average <= LIMIT;
    },
    fullStars(): number {
      // Round fractionals above 0.75 up to the next whole number
      return this.average % 1 > 0.75 ? Math.floor(this.average) + 1 : Math.floor(this.average);
    },
    hasHalfStar(): boolean {
      // Give fractionals between 0.25 and 0.75 a half star
      return this.average % 1 >= 0.25 && this.average % 1 <= 0.75;
    },
    emptyStars(): number {
      // Calculate the number of empty stars based on full and half stars
      return LIMIT - this.fullStars - (this.hasHalfStar ? 1 : 0);
    },
    ariaLabel(): string {
      const ratingText = `Rating: ${this.average.toFixed(1)} out of ${LIMIT} stars.`;
      return this.showCount ? `${ratingText} Total reviews: ${this.count}` : ratingText;
    },
  },
};
</script>
